import "@chatscope/chat-ui-kit-styles/dist/default/styles.min.css";
import {
  MainContainer,
  ChatContainer,
  MessageList,
  Message,
  MessageInput,
  ConversationHeader,
  TypingIndicator,
} from "@chatscope/chat-ui-kit-react";
import { Box, Button, Chip, Stack, TextField, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { sendServerRequest } from "../../server/request";
import { openGlobalSnack } from "../utils/snack";

// ---- types ----

interface InteractiveListOption {
  id: string;
  title: string;
  description: string;
}

interface BotMessage {
  type: string;
  content: any;
}

type MsgKind =
  | { kind: "text"; text: string; direction: "incoming" | "outgoing" }
  | { kind: "interactive"; header: string; body: string; list: InteractiveListOption[] };

interface ChatMsg {
  id: number;
  msg: MsgKind;
}

// ---- message converter ----

function botMessageToKind(m: BotMessage): MsgKind {
  const type = m.type;

  if (type === "text") {
    return { kind: "text", text: String(m.content ?? ""), direction: "incoming" };
  }

  if (type === "interactive") {
    const c = m.content ?? {};
    const list: InteractiveListOption[] = Array.isArray(c.list) ? c.list : [];
    return {
      kind: "interactive",
      header: c.header ?? "",
      body: c.body ?? "",
      list,
    };
  }

  // image, video, audio, document, sticker → show as text with JSON params
  if (typeof m.content === "string") {
    return { kind: "text", text: `[${type}] ${m.content}`, direction: "incoming" };
  }
  if (m.content?.caption) {
    return { kind: "text", text: `[${type}] ${m.content.caption}\n${m.content.link ?? ""}`, direction: "incoming" };
  }
  if (m.content?.link) {
    return { kind: "text", text: `[${type}] ${m.content.link}`, direction: "incoming" };
  }
  return { kind: "text", text: `[${type}] ${JSON.stringify(m.content)}`, direction: "incoming" };
}

// ---- sub-component: interactive list bubble ----

function InteractiveListBubble({
  header,
  body,
  list,
  onSelect,
}: {
  header: string;
  body: string;
  list: InteractiveListOption[];
  onSelect: (id: string, label: string) => void;
}) {
  return (
    <Box
      sx={{
        alignSelf: "flex-start",
        maxWidth: "72%",
        background: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: "8px 8px 8px 2px",
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {header && (
        <Typography variant="subtitle2" fontWeight={700} fontSize={13}>
          {header}
        </Typography>
      )}
      <Typography variant="body2" fontSize={13} sx={{ whiteSpace: "pre-wrap" }}>
        {body}
      </Typography>
      <Stack direction="column" gap={0.5} mt={0.5}>
        {list.map((opt) => (
          <Button
            key={opt.id}
            variant="outlined"
            size="small"
            onClick={() => onSelect(opt.id, opt.title)}
            sx={{ justifyContent: "flex-start", textTransform: "none", fontSize: 12 }}
          >
            <Box>
              <Typography fontSize={12} fontWeight={600}>{opt.title}</Typography>
              {opt.description && (
                <Typography fontSize={11} color="text.secondary">{opt.description}</Typography>
              )}
            </Box>
          </Button>
        ))}
      </Stack>
    </Box>
  );
}

// ---- main component ----

export function TestChat() {
  const [phone, setPhone] = useState("");
  const [state, setState] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [loading, setLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  const idRef = useRef(0);
  const phoneRef = useRef(phone);

  useEffect(() => { phoneRef.current = phone; }, [phone]);

  const addMsg = useCallback((kind: MsgKind) => {
    setMessages((prev) => [...prev, { id: idRef.current++, msg: kind }]);
  }, []);

  const addBotMessages = useCallback((raw: BotMessage[]) => {
    raw.forEach((m) => addMsg(botMessageToKind(m)));
  }, [addMsg]);

  // Poll for async bot responses
  useEffect(() => {
    const interval = setInterval(async () => {
      const p = phoneRef.current.replace(/\D/g, "");
      if (!p) return;
      try {
        const r = await sendServerRequest("GET", `/server/testchat/poll/${p}`);
        if (r.data?.messages?.length) {
          setIsTyping(false);
          addBotMessages(r.data.messages as BotMessage[]);
          const sr = await sendServerRequest("GET", `/server/testchat/state/${p}`);
          if (sr.data?.state) setState(sr.data.state);
        }
      } catch {}
    }, 2500);
    return () => clearInterval(interval);
  }, [addBotMessages]);

  async function doSend(raw: string) {
    const text = raw.replace(/<[^>]*>/g, "").trim();
    const p = phone.replace(/\D/g, "");
    if (!p) { openGlobalSnack("Informe o telefone do cliente.", "warning"); return; }
    if (!text) return;

    addMsg({ kind: "text", text, direction: "outgoing" });
    setLoading(true);
    setIsTyping(true);

    try {
      const r = await sendServerRequest("POST", "/server/testchat/message", {
        phone: p,
        message: text,
      });
      setLoading(false);

      if (!r.data) { setIsTyping(false); return; }
      if (r.data.error) { openGlobalSnack(r.data.error, "error"); setIsTyping(false); return; }

      setState(r.data.state);

      if (r.data.messages?.length) {
        setIsTyping(false);
        addBotMessages(r.data.messages as BotMessage[]);
      }
      // async state: isTyping stays true, poll will resolve
    } catch {
      setLoading(false);
      setIsTyping(false);
      openGlobalSnack("Erro ao enviar mensagem.", "error");
    }
  }

  async function handleInteractiveSelect(id: string, label: string) {
    const p = phone.replace(/\D/g, "");
    if (!p) { openGlobalSnack("Informe o telefone do cliente.", "warning"); return; }

    addMsg({ kind: "text", text: label, direction: "outgoing" });
    setLoading(true);
    setIsTyping(true);

    try {
      const r = await sendServerRequest("POST", "/server/testchat/message", {
        phone: p,
        message: id,
      });
      setLoading(false);

      if (!r.data) { setIsTyping(false); return; }
      if (r.data.error) { openGlobalSnack(r.data.error, "error"); setIsTyping(false); return; }

      setState(r.data.state);

      if (r.data.messages?.length) {
        setIsTyping(false);
        addBotMessages(r.data.messages as BotMessage[]);
      }
    } catch {
      setLoading(false);
      setIsTyping(false);
      openGlobalSnack("Erro ao enviar mensagem.", "error");
    }
  }

  async function handleReset() {
    const p = phone.replace(/\D/g, "");
    if (!p) { openGlobalSnack("Informe o telefone.", "warning"); return; }
    await sendServerRequest("POST", "/server/testchat/reset", { phone: p }).catch(() => {});
    setMessages([]);
    setState(null);
    setIsTyping(false);
    addMsg({ kind: "text", text: "Chat resetado. Envie uma mensagem para começar.", direction: "incoming" });
  }

  async function handlePhoneBlur() {
    const p = phone.replace(/\D/g, "");
    if (!p) return;

    // Carrega a conversa das últimas 48h do mesmo número, se houver.
    const h = await sendServerRequest("GET", `/server/testchat/history/${p}`).catch(() => null);
    const history = (h?.data?.messages ?? []) as Array<BotMessage & { direction?: "incoming" | "outgoing" }>;
    if (history.length) {
      const restored: ChatMsg[] = history.map((m) => {
        const kind = botMessageToKind(m);
        if (kind.kind === "text" && m.direction === "outgoing") {
          return { id: idRef.current++, msg: { ...kind, direction: "outgoing" } };
        }
        return { id: idRef.current++, msg: kind };
      });
      setMessages(restored);
    }

    const r = await sendServerRequest("GET", `/server/testchat/state/${p}`).catch(() => null);
    if (r?.data?.state) {
      setState(r.data.state);
      if (!history.length) {
        addMsg({ kind: "text", text: `Chat existente. Estado atual: ${r.data.state}`, direction: "incoming" });
      }
    }
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 180px)", p: 2, gap: 1.5 }}>
      {/* Phone + state + reset */}
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <TextField
          label="Telefone do cliente (teste)"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          onBlur={handlePhoneBlur}
          size="small"
          placeholder="ex: 47999990000"
          sx={{ flex: 1 }}
        />
        {state && <Chip label={state} size="small" color="primary" variant="outlined" />}
        <Button variant="outlined" color="error" size="small" onClick={handleReset}>
          Resetar
        </Button>
      </Box>

      {/* Chat */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <MainContainer style={{ height: "100%", borderRadius: 8 }}>
          <ChatContainer>
            <ConversationHeader>
              <ConversationHeader.Content userName="Bot FGTS" info="Simulador de cliente real" />
            </ConversationHeader>
            <MessageList
              typingIndicator={isTyping ? <TypingIndicator content="Bot digitando..." /> : undefined}
            >
              {messages.map((m) => {
                if (m.msg.kind === "interactive") {
                  return (
                    <Message
                      key={m.id}
                      model={{ direction: "incoming", position: "single", type: "custom" }}
                    >
                      <Message.CustomContent>
                        <InteractiveListBubble
                          header={m.msg.header}
                          body={m.msg.body}
                          list={m.msg.list}
                          onSelect={handleInteractiveSelect}
                        />
                      </Message.CustomContent>
                    </Message>
                  );
                }
                return (
                  <Message
                    key={m.id}
                    model={{
                      message: m.msg.text,
                      direction: m.msg.direction,
                      position: "single",
                    }}
                  />
                );
              })}
            </MessageList>
            <MessageInput
              placeholder="Digite uma mensagem..."
              onSend={doSend}
              disabled={loading}
              attachButton={false}
            />
          </ChatContainer>
        </MainContainer>
      </Box>
    </Box>
  );
}
